import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Search, History, Share2, Archive, Save, ChevronRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, useApi, fmtDate, EmptyState } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function renderMd(md = "") {
  let s = md.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  s = s.replace(/^### (.*)$/gm, "<h3>$1</h3>").replace(/^## (.*)$/gm, "<h2>$1</h2>").replace(/^# (.*)$/gm, "<h1>$1</h1>");
  s = s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/`(.+?)`/g, "<code>$1</code>");
  s = s.replace(/^- \[x\] (.*)$/gm, "<div>☑ $1</div>").replace(/^- \[ \] (.*)$/gm, "<div>☐ $1</div>").replace(/^- (.*)$/gm, "<li>$1</li>");
  s = s.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`);
  s = s.replace(/\[(.+?)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  return s.replace(/\n{2,}/g, "<br/><br/>");
}

function flatten(nodes, parent = null, depth = 0, out = []) {
  nodes.filter((n) => (n.parent_id || null) === parent).forEach((n) => { out.push({ ...n, depth, hasKids: nodes.some((k) => k.parent_id === n.id) }); flatten(nodes, n.id, depth + 1, out); });
  return out;
}

function Tree({ nodes, sel, onSelect }) {
  return flatten(nodes).map((n) => {
    const cls = sel === n.id ? "bg-blue-600 text-white" : "hover:bg-secondary";
    return <button key={n.id} data-testid={`kb-node-${n.id}`} onClick={() => onSelect(n.id)} className={`w-full text-left text-sm px-2 py-1 rounded flex items-center gap-1 ${cls}`} style={{ paddingLeft: 8 + n.depth * 14 }}>
      {n.hasKids ? <ChevronRight className="h-3 w-3 opacity-60" /> : <FileText className="h-3 w-3 opacity-60" />}
      <span className="truncate">{n.icon ? `${n.icon} ` : ""}{n.title}</span>
    </button>;
  });
}

function Sidebar({ tree, sel, onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  useEffect(() => {
    if (!q) { setResults(null); return undefined; }
    const t = setTimeout(() => api.get(`/knowledge?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  return <div className="bg-card border rounded-lg p-3 space-y-2 lg:col-span-1">
    <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" /><Input data-testid="kb-search" className="pl-7 h-8 text-sm" placeholder="Search articles…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
    {results ? <div className="space-y-1" data-testid="kb-results">
      {results.length === 0 && <p className="text-xs text-muted-foreground px-2">No matches</p>}
      {results.map((r) => <button key={r.id} data-testid={`kb-result-${r.id}`} onClick={() => onSelect(r.id)} className="w-full text-left px-2 py-1 rounded hover:bg-secondary"><div className="text-sm font-medium">{r.title}</div><div className="text-[11px] text-muted-foreground line-clamp-2">{r.snippet}</div></button>)}
    </div> : <div data-testid="kb-tree">
      {tree.length === 0 ? <p className="text-xs text-muted-foreground px-2">No articles yet.</p> : <Tree nodes={tree} sel={sel} onSelect={onSelect} />}
    </div>}
  </div>;
}

function Editor({ edit, setEdit, onSave }) {
  return <div className="space-y-3" data-testid="kb-editor">
    <Input data-testid="kb-edit-title" className="text-lg font-heading font-semibold" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
    <Textarea data-testid="kb-edit-body" rows={18} className="font-mono text-sm" value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} placeholder="Markdown: # Heading, **bold**, - list, - [ ] task, [link](https://…)" />
    <div className="flex gap-2"><Button data-testid="kb-save" onClick={onSave}><Save className="h-4 w-4 mr-1" />Save</Button><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button></div>
  </div>;
}

function Toolbar({ art, can, onEdit, onSub, onVersions, onAct }) {
  return <div className="flex gap-1 flex-wrap justify-end">
    <Button size="sm" variant="ghost" data-testid="kb-fav" onClick={() => onAct("favourite")}><Star className={`h-4 w-4 ${art.favourite ? "fill-amber-400 text-amber-500" : ""}`} /></Button>
    {can("knowledge", "edit") && <Button size="sm" variant="outline" data-testid="kb-edit-btn" onClick={onEdit}>Edit</Button>}
    {can("knowledge", "edit") && <Button size="sm" variant="ghost" data-testid="kb-subpage" onClick={onSub}><Plus className="h-4 w-4" />Sub-page</Button>}
    {can("knowledge", "edit") && <Button size="sm" variant="ghost" title="Versions" data-testid="kb-versions-btn" onClick={onVersions}><History className="h-4 w-4" /></Button>}
    {can("knowledge", "edit") && <Button size="sm" variant="ghost" title={art.share_token ? "Disable public link" : "Share publicly"} data-testid="kb-share" onClick={() => onAct(art.share_token ? "unshare" : "share")}><Share2 className={`h-4 w-4 ${art.share_token ? "text-emerald-600" : ""}`} /></Button>}
    {can("knowledge", "delete") && <Button size="sm" variant="ghost" className="text-red-600" data-testid="kb-archive" onClick={() => window.confirm("Archive this article?") && onAct("archive")}><Archive className="h-4 w-4" /></Button>}
  </div>;
}

function Versions({ versions, onRestore }) {
  return <div className="border rounded p-2 text-xs space-y-1" data-testid="kb-versions">
    {versions.length === 0 ? "No previous versions" : versions.map((v) => <div key={v.n} className="flex justify-between items-center"><span>v{v.n} · {fmtDate(v.at)} · {v.by}</span><Button size="sm" variant="ghost" className="h-6 text-xs" data-testid={`kb-restore-${v.n}`} onClick={() => onRestore(v.n)}>Restore</Button></div>)}
  </div>;
}

export default function KnowledgeView() {
  const { can } = useAuth();
  const tree = useApi("/knowledge");
  const [sel, setSel] = useState(null);
  const [art, setArt] = useState(null);
  const [edit, setEdit] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const load = async (id) => { setSel(id); setEdit(null); setShowVersions(false); try { const { data } = await api.get(`/knowledge/${id}`); setArt(data); } catch (e) { toast.error(errMsg(e)); } };
  const create = async (parent_id = null) => { try { const { data } = await api.post("/knowledge", { title: "Untitled", body: "", parent_id }); tree.reload(); await load(data.id); setEdit({ title: "Untitled", body: "" }); } catch (e) { toast.error(errMsg(e)); } };
  const save = async () => { try { const { data } = await api.put(`/knowledge/${sel}`, { ...edit, parent_id: art.parent_id, icon: art.icon, tags: art.tags }); setArt({ ...art, ...data }); setEdit(null); tree.reload(); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); } };
  const act = async (action, extra = "") => {
    try {
      const { data } = await api.post(`/knowledge/${sel}/${action}${extra}`);
      if (action === "archive") { setArt(null); setSel(null); tree.reload(); return; }
      await load(sel);
      if (action === "share" && data.share_token) { const url = `${window.location.origin}/kb/${data.share_token}`; navigator.clipboard?.writeText(url); toast.success("Public link copied: " + url); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <div className="space-y-4" data-testid="knowledge-view">
    <PageHeader eyebrow="Productivity" title="Knowledge" subtitle="Company wiki: procedures, policies, how-tos" actions={can("knowledge", "create") && <Button data-testid="kb-new-btn" onClick={() => create(null)}><Plus className="h-4 w-4 mr-1" />New article</Button>} />
    <div className="grid lg:grid-cols-4 gap-4">
      <Sidebar tree={tree.data || []} sel={sel} onSelect={load} />
      <div className="lg:col-span-3 bg-card border rounded-lg p-5 min-h-[60vh]">
        {!art && <EmptyState title="Pick an article" hint="Select from the tree or create a new one." />}
        {art && edit && <Editor edit={edit} setEdit={setEdit} onSave={save} />}
        {art && !edit && <div className="space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div><h2 className="text-2xl font-heading font-semibold" data-testid="kb-title">{art.title}</h2><div className="text-xs text-muted-foreground">Updated {fmtDate(art.updated_at)} by {art.editor_name || art.author_name} · {art.views} views{art.share_token && <span className="ml-2 text-emerald-700">· public link on</span>}</div></div>
            <Toolbar art={art} can={can} onEdit={() => setEdit({ title: art.title, body: art.body || "" })} onSub={() => create(art.id)} onVersions={() => setShowVersions(!showVersions)} onAct={act} />
          </div>
          {showVersions && <Versions versions={art.versions} onRestore={(n) => act("restore", `?n=${n}`)} />}
          <div className="prose prose-sm max-w-none dark:prose-invert" data-testid="kb-body" dangerouslySetInnerHTML={{ __html: renderMd(art.body) || "<i class='text-muted-foreground'>Empty page — click Edit.</i>" }} />
        </div>}
      </div>
    </div>
  </div>;
}
